import { useRef, useState } from 'react';
import { Upload, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { adminUploadImage } from '../../../lib/adminApi';
import { resolveImageUrl } from '../../../lib/imageUrl';

// Re-exported for other admin components that still import from here.
export { resolveImageUrl };

export default function ImagePicker({ value, onChange, label = 'Image', size = 'md' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const boxClasses = {
    sm: 'w-14 h-14',
    md: 'w-20 h-20',
    lg: 'w-28 h-28',
  }[size] || 'w-20 h-20';

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      onChange(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed.';
      setError(typeof detail === 'string' ? detail : 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const preview = resolveImageUrl(value, 400);

  return (
    <div>
      <div className="text-sm font-medium text-brand-ink mb-1.5">{label}</div>
      <div className="flex items-start gap-3">
        <div
          className={`${boxClasses} rounded-2xl border border-brand-ink/10 bg-brand-cream/50 grid place-items-center overflow-hidden shrink-0`}
          data-testid="image-picker-preview"
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <ImageIcon className="w-6 h-6 text-brand-ink/30" aria-hidden="true" />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-cream hover:bg-brand-teal disabled:opacity-70 px-3 py-1.5 text-sm font-medium"
            >
              {uploading ? (
                <>Uploading <Loader2 className="w-3.5 h-3.5 animate-spin" /></>
              ) : (
                <><Upload className="w-3.5 h-3.5" /> Upload image</>
              )}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="text-xs text-brand-ink/60 hover:text-red-600 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste an image URL"
            className="w-full rounded-full border border-brand-ink/15 px-3 py-1.5 text-xs text-brand-ink placeholder:text-brand-ink/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink/30"
          />
          {error && (
            <div className="text-xs text-red-700 inline-flex items-center gap-1" role="alert">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
